import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Copy, Check, Users, Wallet, ExternalLink, Star, X, MessageCircle, Send, ChevronDown, ChevronUp, TrendingUp, Zap } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useReferralStats, useReferralList } from '../hooks/useApi';
import { AppLang } from '../i18n';
import './ReferralPage.css';
import { openTgLink } from '../lib/openTgLink';

interface ReferralPageProps {
  user: any;
  lang: AppLang;
  onBack: () => void;
}

export const ReferralPage = ({ user: _user, lang: _lang, onBack }: ReferralPageProps) => {
  void _user;
  void _lang;
  const { data: stats, loading } = useReferralStats();
  const { data: referralList } = useReferralList();
  const [copied, setCopied] = useState(false);
  const [copiedSite, setCopiedSite] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [listExpanded, setListExpanded] = useState(false);

  const isPartner = stats?.is_partner ?? false;
  const partnerPct = stats?.partner_percentage ?? 0;
  const rewardPct = isPartner ? partnerPct : (stats?.ref_reward_percent ?? 0);
  const refDays = stats?.ref_bonus_days ?? 30;
  const balanceRub = (stats?.balance_kopecks ?? 0) / 100;
  const totalEarnedRub = (stats?.total_earned_kopecks ?? 0) / 100;
  const withdrawalMinRub = stats?.withdrawal_min_rub ?? 250;
  const canWithdraw = balanceRub >= withdrawalMinRub;
  const bonusMonths = stats?.bonus_months ?? 0;
  const hasBalance = balanceRub > 0;

  const handleCopy = useCallback(() => {
    if (!stats?.referral_link) return;
    navigator.clipboard.writeText(stats.referral_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [stats?.referral_link]);

  const handleCopySite = useCallback(() => {
    if (!stats?.site_link) return;
    navigator.clipboard.writeText(stats.site_link);
    setCopiedSite(true);
    setTimeout(() => setCopiedSite(false), 2000);
  }, [stats?.site_link]);

  const handleWithdraw = useCallback(() => {
    openTgLink('https://t.me/Patrik_vpn_support');
  }, []);

  const handleTelegramShare = useCallback(() => {
    if (!stats?.referral_link) return;
    const text = encodeURIComponent(
      `Подключай VPN от PATRiK VPN — быстрый, стабильный, без ограничений!\n${stats.referral_link}`
    );
    window.open(`https://t.me/share/url?url=${encodeURIComponent(stats.referral_link)}&text=${text}`, '_blank');
  }, [stats?.referral_link]);

  const visibleReferrals = listExpanded ? referralList : referralList.slice(0, 3);
  const hasMoreReferrals = referralList.length > 3;

  if (loading) {
    return (
      <div className="rp">
        <div className="rp-header">
          <button className="rp-back" onClick={onBack}><ArrowLeft size={20} /></button>
        </div>
        <div className="rp-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`rp ${isPartner ? 'rp--partner' : ''}`}>
      <div className="rp-header">
        <button className="rp-back" onClick={onBack}><ArrowLeft size={20} /></button>
      </div>

      <motion.div className="rp-content" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>

        {isPartner && (
          <div className="rp-hero">
            <div className="rp-hero-glow" />
            <div className="rp-hero-badge">
              <Star size={14} />
              Расширенная реферальная система
            </div>
            <div className="rp-hero-pct">{partnerPct}%</div>
            <div className="rp-hero-desc">с каждого платежа ваших рефералов</div>
            <div className="rp-hero-perks">
              <div className="rp-hero-perk">
                <TrendingUp size={14} />
                <span>Процент с каждой оплаты</span>
              </div>
              <div className="rp-hero-perk">
                <Zap size={14} />
                <span>Без лимита на количество</span>
              </div>
            </div>
          </div>
        )}

        {true && (
          <div className={`rp-balance ${isPartner ? 'rp-balance--partner' : ''}`}>
            <div className="rp-balance-label">Баланс</div>
            <div className="rp-balance-value">{balanceRub.toFixed(0)} <span className="rp-balance-currency">руб.</span></div>
          </div>
        )}

        {!isPartner && (
          <div className="rp-reward-note">
            За каждого друга, оплатившего подписку, тебе <b>+{refDays} дней</b> подписки
            <span className="rp-reward-sub">Начисляется один раз с первой оплаты друга</span>
          </div>
        )}

        <div className="rp-stats">
          <div className="rp-stat">
            <span className="rp-stat-value">{stats?.total_referrals ?? 0}</span>
            <span className="rp-stat-label">Приглашено</span>
          </div>
          <div className="rp-stat">
            <span className="rp-stat-value">{stats?.active_referrals ?? 0}</span>
            <span className="rp-stat-label">Активных</span>
          </div>
          <div className="rp-stat">
            <span className="rp-stat-value">{totalEarnedRub.toFixed(0)}</span>
            <span className="rp-stat-label">Заработано, руб.</span>
          </div>
        </div>

        <div className="rp-conditions">
          <div className="rp-conditions-title">Как это работает</div>
          <div className="rp-conditions-list">
            <div className="rp-condition">
              <span className="rp-condition-num">1</span>
              <span>Поделись ссылкой с другом</span>
            </div>
            <div className="rp-condition">
              <span className="rp-condition-num">2</span>
              <span>Друг оплачивает подписку</span>
            </div>
            {isPartner ? (
              <>
                <div className="rp-condition">
                  <span className="rp-condition-num">3</span>
                  <span>Ты получаешь <b>{rewardPct}%</b> от суммы платежа</span>
                </div>
                <div className="rp-condition">
                  <span className="rp-condition-num">4</span>
                  <span>Начисляется <b>один раз</b> — с первой оплаты каждого приглашённого</span>
                </div>
              </>
            ) : (
              <>
                <div className="rp-condition">
                  <span className="rp-condition-num">3</span>
                  <span>Тебе <b>+{refDays} дней</b> подписки — автоматически</span>
                </div>
                <div className="rp-condition">
                  <span className="rp-condition-num">4</span>
                  <span>Бонус — за первую оплату каждого приглашённого друга</span>
                </div>
              </>
            )}
          </div>
          <div className="rp-conditions-note">
            {isPartner
              ? 'Процент начисляется на баланс автоматически один раз с первой оплаты каждого приглашённого. Балансом можно оплатить свою подписку.'
              : `${refDays} дней начисляются автоматически сразу после первой оплаты друга — подписка продлевается без твоего участия. Проценты не начисляются.`}
          </div>
        </div>

        <div className="rp-link-box" onClick={handleCopy}>
          <div className="rp-link-info">
            <span className="rp-link-url">
              {stats?.referral_link
                ? (stats.referral_link.length > 34
                  ? stats.referral_link.slice(0, 34) + '...'
                  : stats.referral_link)
                : '...'}
            </span>
            <span className="rp-link-label">🤖 Ссылка на бота</span>
          </div>
          <button className={`rp-link-copy ${copied ? 'rp-link-copy--copied' : ''}`}>
            {copied ? <Check size={18} /> : <Copy size={18} />}
          </button>
        </div>

        <div className="rp-link-box" onClick={handleCopySite}>
          <div className="rp-link-info">
            <span className="rp-link-url">
              {stats?.site_link
                ? (stats.site_link.length > 34
                  ? stats.site_link.slice(0, 34) + '...'
                  : stats.site_link)
                : '...'}
            </span>
            <span className="rp-link-label">🌐 Ссылка на сайт</span>
          </div>
          <button className={`rp-link-copy ${copiedSite ? 'rp-link-copy--copied' : ''}`}>
            {copiedSite ? <Check size={18} /> : <Copy size={18} />}
          </button>
        </div>

        <div className="rp-share-row">
          <button className="rp-share-btn rp-share-btn--tg" onClick={handleTelegramShare}>
            <Send size={16} />
            Отправить в Telegram
          </button>
          <button className="rp-share-btn rp-share-btn--qr" onClick={() => setQrOpen(true)}>
            QR
          </button>
        </div>

        {!isPartner && (
          <div className="rp-extended" onClick={() => setSheetOpen(true)}>
            <div className="rp-extended-icon"><Star size={20} /></div>
            <div className="rp-extended-info">
              <div className="rp-extended-title">Расширенная партнёрка</div>
              <div className="rp-extended-sub">Зарабатывай процент с каждого платежа</div>
            </div>
            <ExternalLink size={16} className="rp-extended-arrow" />
          </div>
        )}

        {referralList.length > 0 && (
          <div className="rp-ref-list">
            <div className="rp-ref-list-title">
              <Users size={16} />
              Мои рефералы
              <span className="rp-ref-list-count">{referralList.length}</span>
            </div>
            <div className="rp-ref-list-items">
              {visibleReferrals.map((ref) => (
                <div key={ref.user_id} className="rp-ref-item">
                  <div className="rp-ref-item-avatar">
                    {(ref.first_name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="rp-ref-item-info">
                    <span className="rp-ref-item-name">{ref.first_name}</span>
                    <span className="rp-ref-item-meta">
                      {ref.joined_at
                        ? new Date(ref.joined_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
                        : ''}
                      {ref.earned_from_kopecks > 0 && (
                        <> &middot; +{(ref.earned_from_kopecks / 100).toFixed(0)} руб.</>
                      )}
                      {(ref.bonus_months ?? 0) > 0 && (
                        <> &middot; +{ref.bonus_months} мес.</>
                      )}
                    </span>
                  </div>
                  <div className={`rp-ref-item-status ${ref.has_active_sub ? 'rp-ref-item-status--active' : ''}`}>
                    {ref.has_active_sub ? 'Активен' : 'Неактивен'}
                  </div>
                </div>
              ))}
            </div>
            {hasMoreReferrals && (
              <button className="rp-ref-list-toggle" onClick={() => setListExpanded(!listExpanded)}>
                {listExpanded ? (
                  <>Свернуть <ChevronUp size={16} /></>
                ) : (
                  <>Показать всех ({referralList.length}) <ChevronDown size={16} /></>
                )}
              </button>
            )}
          </div>
        )}

        <div className="rp-earnings">
          <div className="rp-earnings-title">
            <Wallet size={16} />
            История начислений
          </div>
          {(!stats?.earnings || stats.earnings.length === 0) ? (
            <div className="rp-empty">
              Пока нет начислений. Пригласи друга!
            </div>
          ) : (
            <div className="rp-earnings-list">
              {stats.earnings.map((e) => (
                <div key={e.id} className="rp-earning-item">
                  <div className="rp-earning-info">
                    <span className="rp-earning-amount">
                      {e.earning_type === 'months' ? `+${refDays} дней` : `+${(e.amount_kopecks / 100).toFixed(0)} руб.`}
                    </span>
                    <span className="rp-earning-type">
                      {e.earning_type === 'percent'
                        ? `Партнёрское (${partnerPct}%)`
                        : e.earning_type === 'months'
                          ? 'Реферальный бонус'
                          : 'Реферальное'}
                    </span>
                  </div>
                  <span className="rp-earning-date">
                    {e.created_at ? new Date(e.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {createPortal(
        <AnimatePresence>
          {qrOpen && (
            <>
              <motion.div
                className="rp-sheet-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setQrOpen(false)}
              />
              <motion.div
                className="rp-sheet rp-sheet--qr"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              >
                <div className="rp-sheet-handle" />
                <button className="rp-sheet-close" onClick={() => setQrOpen(false)}>
                  <X size={20} />
                </button>
                <div className="rp-sheet-content rp-qr-content">
                  <h3 className="rp-sheet-title">QR-код</h3>
                  <p className="rp-qr-desc">Покажи другу — он отсканирует и перейдёт по ссылке</p>
                  <div className="rp-qr-wrapper">
                    <QRCodeSVG
                      value={stats?.referral_link || ''}
                      size={200}
                      bgColor="#ffffff"
                      fgColor="#0a1628"
                      level="M"
                    />
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}

      {createPortal(
        <AnimatePresence>
          {sheetOpen && (
            <>
              <motion.div
                className="rp-sheet-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSheetOpen(false)}
              />
              <motion.div
                className="rp-sheet"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              >
                <div className="rp-sheet-handle" />
                <button className="rp-sheet-close" onClick={() => setSheetOpen(false)}>
                  <X size={20} />
                </button>

                <div className="rp-sheet-content">
                  <div className="rp-sheet-badge"><Star size={16} /> Партнёрская программа</div>
                  <h3 className="rp-sheet-title">Зарабатывай больше</h3>
                  <p className="rp-sheet-desc">
                    Получай процент от каждого платежа приглашённых друзей.
                    Условия обсуждаются индивидуально.
                  </p>

                  <div className="rp-sheet-features">
                    <div className="rp-sheet-feature">
                      <span className="rp-sheet-feature-icon">%</span>
                      <div>
                        <div className="rp-sheet-feature-title">Процент с платежей</div>
                        <div className="rp-sheet-feature-desc">До 50% от каждой оплаты твоих рефералов</div>
                      </div>
                    </div>
                    <div className="rp-sheet-feature">
                      <span className="rp-sheet-feature-icon">&#8734;</span>
                      <div>
                        <div className="rp-sheet-feature-title">Без ограничений</div>
                        <div className="rp-sheet-feature-desc">Награда с каждого платежа, не только с первого</div>
                      </div>
                    </div>
                    <div className="rp-sheet-feature">
                      <span className="rp-sheet-feature-icon">&#9733;</span>
                      <div>
                        <div className="rp-sheet-feature-title">Индивидуальные условия</div>
                        <div className="rp-sheet-feature-desc">Процент и условия обсуждаются лично</div>
                      </div>
                    </div>
                  </div>

                  <button
                    className="rp-sheet-cta"
                    onClick={() => {
                      const msg = encodeURIComponent(
                        'Здравствуйте! Хочу присоединиться к расширенной партнёрской программе PATRiK VPN — интересует сотрудничество на условиях процента с платежей рефералов. Готов обсудить детали.'
                      );
                      window.open(`https://t.me/Patrik_vpn_support?text=${msg}`, '_blank');
                      setSheetOpen(false);
                    }}
                  >
                    <MessageCircle size={18} />
                    Написать в поддержку
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
};
